import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import SpinnerBox from '../components/Spinner/SpinnerBox';
import Btn from '../components/Btn/Btn';
import { getStoredState, getStoredCodeVerifier, useAuth } from '../contexts/AuthContext';
import { AUTH_CONFIG } from '../utils/authConfig';
import './FrontPage.css';

/**
 * Decode JWT token (base64url decoding)
 */
function decodeJwt(token: string): Record<string, unknown> {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid JWT format');
        }

        // Decode base64url (JWT uses base64url, not standard base64)
        const payload = parts[1];
        // Replace URL-safe characters and add padding if needed
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const decoded = atob(padded);
        return JSON.parse(decoded);
    } catch (error) {
        throw new Error(`Failed to decode JWT: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

const AuthCallback = (): JSX.Element => {
    const history = useHistory();
    const { completeLogin } = useAuth();
    const [statusMessage, setStatusMessage] = useState('Finishing sign in…');
    const [error, setError] = useState<string | null>(null);
    const runOnce = useRef(false);

    useEffect(() => {
        // Prevent React Strict Mode double-invoke
        if (runOnce.current) return;
        runOnce.current = true;

        const finalize = async () => {
            // With response_mode=fragment, OAuth response comes in URL hash, not query params
            // This is more secure as fragments are not sent to the server
            // Fallback to query params for backward compatibility if hash is empty
            const hash = window.location.hash.substring(1); // Remove leading '#'
            const search = window.location.search.substring(1); // Remove leading '?'
            const hashOrSearch = hash || search;
            const params = new URLSearchParams(hashOrSearch);

            // Clean up URL immediately after reading (remove hash/query for security)
            // This ensures authorization code doesn't remain in URL/history
            if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', window.location.pathname);
            }

            // Check for OAuth error first
            const error = params.get('error');
            const errorDescription = params.get('error_description');

            if (error) {
                const decodedErrorDescription = errorDescription
                    ? decodeURIComponent(errorDescription)
                    : 'An authentication error occurred';
                setError(`Authentication failed: ${decodedErrorDescription}`);
                setStatusMessage('Unable to sign in.');
                console.error('[AuthCallback] OAuth error received', {
                    error,
                    errorDescription: decodedErrorDescription,
                });
                // Ensure we redirect to a clean error state
                return;
            }

            const authCode = params.get('code');
            const returnedState = params.get('state');
            const storedState = getStoredState();

            if (!authCode || !returnedState || !storedState) {
                setError('Missing authorization information. Please try signing in again.');
                setStatusMessage('Unable to sign in.');
                console.error('[AuthCallback] Missing required OAuth values', {
                    hasAuthCode: Boolean(authCode),
                    returnedState,
                    storedStateExists: Boolean(storedState),
                });
                return;
            }

            // Verify state matches (CSRF protection)
            if (returnedState !== storedState) {
                setError('Security verification failed. Please try signing in again.');
                setStatusMessage('Unable to sign in.');
                console.error('[AuthCallback] State mismatch detected', { returnedState, storedState });
                return;
            }

            // Get stored code verifier for PKCE
            const codeVerifier = getStoredCodeVerifier();
            if (!codeVerifier) {
                setError('Missing PKCE code verifier. Please try signing in again.');
                setStatusMessage('Unable to sign in.');
                console.error('[AuthCallback] Missing code verifier');
                return;
            }

            // Verify we can regenerate the challenge from the verifier
            // AND compare it with what was actually sent to Keycloak
            try {
                const { generateCodeChallenge } = await import('../utils/pkce');
                const regeneratedChallenge = await generateCodeChallenge(codeVerifier);

                // Get the challenge that was actually sent to Keycloak
                const storedChallenge = sessionStorage.getItem('koala.sso.code_challenge');

                if (regeneratedChallenge !== storedChallenge) {
                    console.error('[AuthCallback] CRITICAL: Regenerated challenge does NOT match stored challenge!', {
                        regenerated: regeneratedChallenge,
                        stored: storedChallenge,
                        verifierUsed: codeVerifier.substring(0, 50),
                    });
                }
            } catch (verifyError) {
                console.error('[AuthCallback] Failed to regenerate challenge', verifyError);
            }

            try {
                // Exchange authorization code for tokens using PKCE
                // Use proxy endpoint to avoid CORS issues with Keycloak
                const tokenEndpoint = '/api/auth/token';

                // Build form data using URLSearchParams to ensure consistent encoding
                const bodyParams = new URLSearchParams();
                bodyParams.append('grant_type', 'authorization_code');
                bodyParams.append('code', authCode);
                bodyParams.append('redirect_uri', AUTH_CONFIG.REDIRECT_URI);
                bodyParams.append('client_id', AUTH_CONFIG.CLIENT_ID);
                bodyParams.append('code_verifier', codeVerifier);

                const bodyString = bodyParams.toString();

                // Verify the code_verifier in the final body string
                const verifierFromParams = bodyParams.get('code_verifier');
                const verifierMatches = verifierFromParams === codeVerifier;

                if (!verifierMatches) {
                    console.error('[AuthCallback] CRITICAL: Code verifier mismatch in final body!', {
                        original: codeVerifier,
                        inBody: verifierFromParams,
                        originalLength: codeVerifier.length,
                        inBodyLength: verifierFromParams?.length,
                        fullBody: bodyString.substring(0, 500),
                    });
                    throw new Error('Code verifier encoding error - values do not match');
                }

                const response = await fetch(tokenEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: bodyString,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('[AuthCallback] Token exchange failed', errorData);
                    throw new Error(
                        errorData.error_description || errorData.error || 'Failed to exchange authorization code.',
                    );
                }

                const tokenData = await response.json();
                const { access_token, id_token, refresh_token } = tokenData;

                // Decode JWT tokens to extract user info
                let userInfo: Record<string, unknown> = {};

                if (id_token) {
                    try {
                        const decodedIdToken = decodeJwt(id_token);
                        userInfo = {
                            sub: decodedIdToken.sub,
                            sid: decodedIdToken.sid,
                            name: decodedIdToken.name,
                            given_name: decodedIdToken.given_name,
                            family_name: decodedIdToken.family_name,
                            email: decodedIdToken.email,
                            preferred_username: decodedIdToken.preferred_username,
                        };
                    } catch (decodeError) {
                        console.warn('[AuthCallback] Failed to decode id_token:', decodeError);
                    }
                }

                // If we have access_token but no user info from id_token, try to decode access_token
                if (access_token && !userInfo.sub) {
                    try {
                        const decodedAccessToken = decodeJwt(access_token);
                        userInfo = {
                            sub: decodedAccessToken.sub || userInfo.sub,
                            sid: decodedAccessToken.sid || userInfo.sid,
                            ...userInfo,
                        };
                    } catch (decodeError) {
                        console.warn('[AuthCallback] Failed to decode access_token:', decodeError);
                    }
                }

                const profile = {
                    ...userInfo,
                    access_token,
                    id_token,
                    refresh_token,
                    expires_in: tokenData.expires_in,
                    token_type: tokenData.token_type,
                    scope: tokenData.scope,
                };

                completeLogin(profile);
                history.replace('/');
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Unable to finish sign in.';
                setError(message);
                setStatusMessage('Unable to finish sign in.');
                console.error('[AuthCallback] Unexpected error while completing login', err);
            }
        };

        finalize();
    }, [completeLogin, history]);

    return (
        <div className="align-everything" style={{ minHeight: '100vh' }}>
            {!error ? (
                <>
                    <SpinnerBox />
                    <p className="center-text">{statusMessage}</p>
                </>
            ) : (
                <div className="center-text" style={{ marginTop: '1rem' }}>
                    <p>{statusMessage}</p>
                    <p>{error}</p>
                    <Btn title="Back to start" variant="secondary" type="button" onClick={() => history.replace('/')} />
                </div>
            )}
        </div>
    );
};

export default AuthCallback;
